/**
 * The prompt box's one composer (docs/refactor/06-compositor.md): instead of modes, what the person
 * types says what to do —
 *
 *   `@time`, `@outra-maquina`, `@<a computer>`, `@<an agent listening>`  who does it (default: this machine's agent)
 *   `/analisar`, `/executar`, …                         a command from the catalog, or of the app
 *   `!git status`                                       a command in the terminal of the project
 *   anything else                                       a prompt for the agent
 *
 * Plain functions, no I/O: the prompt box uses them to suggest and to decide, and the tests read them.
 */

export type Target =
  | { kind: 'default' }
  | { kind: 'team' }
  | { kind: 'elsewhere' }
  | { kind: 'listener'; id: string }
  | { kind: 'machine'; id: string; name: string }
  | { kind: 'agent'; agent: 'claude' | 'claude-2' | 'codex' | 'gemini' | 'opencode' }

/** One of the person's other computers with the desktop app on: `@<its name>` sends there. */
export interface Machine {
  id: string
  name: string
}

export interface ComposerOptions {
  desktop: boolean
  /** The person's other computers that are on (not this one). */
  machines?: Machine[]
}

export interface SlashCommand {
  /** What is typed after the slash. */
  name: string
  labelKey: string
  hintKey: string
}

/** The commands, in the order they are offered. Each maps to something the app already does. */
export const SLASH_COMMANDS: SlashCommand[] = [
  { name: 'executar', labelKey: 'composer.cmd.run', hintKey: 'composer.cmd.runHint' },
  { name: 'publicar', labelKey: 'composer.cmd.publish', hintKey: 'composer.cmd.publishHint' },
  { name: 'analisar', labelKey: 'composer.cmd.analyze', hintKey: 'composer.cmd.analyzeHint' },
  { name: 'o-que-falta', labelKey: 'composer.cmd.launch', hintKey: 'composer.cmd.launchHint' },
  { name: 'pendencias', labelKey: 'composer.cmd.pending', hintKey: 'composer.cmd.pendingHint' },
  { name: 'auditar', labelKey: 'composer.cmd.audit', hintKey: 'composer.cmd.auditHint' },
  { name: 'testar', labelKey: 'composer.cmd.test', hintKey: 'composer.cmd.testHint' },
  { name: 'commit', labelKey: 'composer.cmd.commit', hintKey: 'composer.cmd.commitHint' },
  { name: 'nova', labelKey: 'composer.cmd.new', hintKey: 'composer.cmd.newHint' },
]

/**
 * The commands that are a prompt of the server's catalog (docs/refactor/06-compositor.md): they go to
 * the agent like anything written in the box — here on the desktop, streamed; to the computer at home
 * from the web — with the catalog's text instead of the person's.
 */
export const CATALOG_COMMANDS: Record<string, string> = { testar: 'run.test', commit: 'run.commit' }

export interface Listener {
  id: string
  label: string
}

/** The names `@` accepts: the team, another of one's machines (any, or one by name), and the agents listening by name. */
export function mentionOptions(listeners: Listener[], options: ComposerOptions): Array<{ name: string; target: Target; label: string }> {
  return [
    { name: 'time', target: { kind: 'team' }, label: 'time' },
    ...(options.desktop ? [{ name: 'outra-maquina', target: { kind: 'elsewhere' } as Target, label: 'outra-maquina' }] : []),
    // Which agent does this one prompt, here: "@codex revise isto" (the default stays the one chosen in Conectar agente).
    ...(options.desktop ? (['claude', 'codex', 'gemini', 'opencode'] as const).map((agent) => ({ name: agent, target: { kind: 'agent', agent } as Target, label: agent })) : []),
    // Each of the person's computers by name: "@casa-pc" runs it on that one, and only there.
    ...(options.machines ?? []).filter((m) => slug(m.name)).map((m) => ({ name: slug(m.name), target: { kind: 'machine', id: m.id, name: m.name } as Target, label: m.name })),
    ...listeners.map((l) => ({ name: slug(l.label), target: { kind: 'listener', id: l.id } as Target, label: l.label })),
  ]
}

export function slug(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export type Parsed =
  | { kind: 'shell'; command: string }
  | { kind: 'slash'; command: SlashCommand | null; name: string; rest: string }
  | { kind: 'prompt'; target: Target; text: string }

/**
 * What the text asks for. A leading `!` is the terminal; a leading `/name` is a command; `@name` at the
 * start chooses who does the prompt (the rest is the prompt). An `@` that matches nothing stays in the
 * text: it may be part of what the person wants to say.
 */
export function parse(input: string, listeners: Listener[], options: ComposerOptions): Parsed {
  const text = input.trim()
  if (text.startsWith('!')) return { kind: 'shell', command: text.slice(1).trim() }
  if (text.startsWith('/')) {
    const [head = '', ...rest] = text.slice(1).split(/\s+/)
    const name = head.toLowerCase()
    return { kind: 'slash', command: SLASH_COMMANDS.find((c) => c.name === name) ?? null, name, rest: rest.join(' ') }
  }
  const mention = /^@([^\s]+)\s*([\s\S]*)$/.exec(text)
  if (mention) {
    const name = slug(mention[1]!)
    const found = mentionOptions(listeners, options).find((o) => o.name === name)
    if (found) return { kind: 'prompt', target: found.target, text: mention[2]!.trim() }
  }
  return { kind: 'prompt', target: { kind: 'default' }, text }
}

export interface Suggestion {
  /** What replaces the word being typed. */
  insert: string
  label: string
  hintKey?: string
  hint?: string
}

/**
 * What to offer while the person types the first word: commands after `/`, names after `@`. Empty
 * when the cursor is past the first word or nothing matches.
 */
export function suggestions(input: string, cursor: number, listeners: Listener[], options: ComposerOptions): Suggestion[] {
  const before = input.slice(0, cursor)
  if (/\s/.test(before.trimStart())) return []
  const word = before.trimStart()
  if (word.startsWith('/')) {
    const typed = word.slice(1).toLowerCase()
    return SLASH_COMMANDS.filter((c) => c.name.startsWith(typed)).map((c) => ({ insert: `/${c.name} `, label: `/${c.name}`, hintKey: c.hintKey }))
  }
  if (word.startsWith('@')) {
    const typed = slug(word.slice(1))
    return mentionOptions(listeners, options)
      .filter((o) => o.name.startsWith(typed))
      .map((o) => ({
        insert: `@${o.name} `,
        label: `@${o.name}`,
        hintKey: o.target.kind === 'listener' ? undefined : o.target.kind === 'machine' ? 'composer.at.machine' : o.target.kind === 'agent' ? 'composer.at.agent' : `composer.at.${o.name}`,
        hint: o.target.kind === 'listener' ? o.label : undefined,
      }))
  }
  return []
}

/** Puts a chosen suggestion in place of the first word. */
export function applySuggestion(input: string, suggestion: Suggestion): string {
  const leading = input.length - input.trimStart().length
  const rest = input.trimStart().replace(/^\S*\s?/, '')
  return input.slice(0, leading) + suggestion.insert + rest
}

/** Names the person gave their agents' tabs ("Backend", "Testes"), by lane. */
export function readLaneNames(storage: Pick<Storage, 'getItem'> | null): Record<number, string> {
  try {
    const parsed: unknown = JSON.parse(storage?.getItem('devpulse.laneNames') ?? '{}')
    if (!parsed || typeof parsed !== 'object') return {}
    return Object.fromEntries(Object.entries(parsed as Record<string, unknown>).filter(([, v]) => typeof v === 'string' && v.trim()).map(([k, v]) => [Number(k), (v as string).slice(0, 24)]))
  } catch {
    return {}
  }
}

/** /executar: the header's ▶ Executar runs (components/RunBar.tsx listens). */
export const RUN_ACTION_EVENT = 'devpulse:run-action'
/** /publicar: the arrow beside ▶ publishes (components/PublishMenu.tsx listens). */
export const PUBLISH_ACTION_EVENT = 'devpulse:publish-action'
/** /analisar, /o-que-falta, /pendencias: the header's Ações do it (components/ProjectActions.tsx listens). */
export const PROJECT_ACTION_EVENT = 'devpulse:project-action'
/** Where the agents' tab names are kept on this machine. */
export const LANE_NAMES_KEY = 'devpulse.laneNames'
